import React, { useEffect, useState } from "react";
import { Alert, Button, IconButton, Popover, TextField, Tooltip } from "@mui/material";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import { companiesApi } from "@/services/api";
import { useScope } from "@/hooks/useScope";
import { COMPANY_CIO_NOTE } from "@/constants/scopeCodes";

interface Props {
  companyId: number;
  companyName: string;
  cioNote?: string | null;
  /** Called with what was stored, so the row updates without refetching the list. */
  onSaved: (companyId: number, cioNote: string | null) => void;
}

/**
 * The CIO's standing instruction for a company, shown on the order ticket.
 *
 * The note is an ordinary company field that RAW authors and both masters hold, so it is
 * read and written through the company endpoint; saving it announces the company back to
 * RAW like any other shared field. The icon renders on every row — dimmed when there is
 * no note — because the note is added from here.
 *
 * ``company_cio_note`` covers seeing a note and editing it alike, and gates this icon
 * alone: the save is an ordinary company PATCH, so the server asks for company-master
 * write. Anyone granted the note scope needs that one too, or the save is refused.
 */
export const CioNotePopover: React.FC<Props> = ({ companyId, companyName, cioNote, onSaved }) => {
  const canEditNote = useScope(COMPANY_CIO_NOTE);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState(cioNote ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reopening after a save elsewhere, or on another company's row, must show the current
  // note rather than a stale draft.
  useEffect(() => {
    if (anchor) {
      setDraft(cioNote ?? "");
      setError(null);
    }
  }, [anchor, cioNote]);

  const hasNote = Boolean(cioNote);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const saved = await companiesApi.patchCompany(companyId, { cio_note: draft.trim() || null });
      onSaved(companyId, saved.cio_note ?? null);
      setAnchor(null);
    } catch {
      setError("Could not save the note. Try again.");
    } finally {
      setSaving(false);
    }
  };

  // After the hooks, never before them.
  if (!canEditNote) return null;

  return (
    <>
      <Tooltip title={hasNote ? "CIO note" : "Add a CIO note"}>
        <IconButton
          size="small"
          aria-label={hasNote ? `CIO note for ${companyName}` : `Add a CIO note for ${companyName}`}
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{ padding: "2px", marginLeft: "2px", color: hasNote ? "#d97706" : "#cbd5e1" }}
        >
          <StickyNote2OutlinedIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <div style={{ padding: 12, fontSize: 12, width: 420 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>CIO Note — {companyName}</div>

          <TextField
            multiline
            minRows={6}
            maxRows={16}
            fullWidth
            size="small"
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            slotProps={{ htmlInput: { style: { fontSize: 12 } } }}
          />
          <div style={{ color: "#64748b", marginTop: 4 }}>
            Shared with RAW. Clearing the box withdraws the note.
          </div>
          {error && (
            <Alert severity="error" sx={{ marginTop: 1, fontSize: 12, padding: "0 8px" }}>
              {error}
            </Alert>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, marginTop: 8 }}>
            <Button size="small" onClick={() => setAnchor(null)} disabled={saving}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleSave}
              disabled={saving || draft === (cioNote ?? "")}
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      </Popover>
    </>
  );
};

export default CioNotePopover;
